import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Shield, ShieldCheck, ShieldAlert, Hash, Link, Clock,
  User, RefreshCw, Search, ChevronDown, ChevronRight,
  Copy, CheckCheck, Loader, Filter, Cpu, AlertTriangle,
  FileKey, Activity, GitCommit, Download, FileText, FileSpreadsheet
} from 'lucide-react';
import { API, apiFetch } from '../lib/api';



/* ── Export helpers ──────────────────────────────────────────────────────── */
function exportCSV(blocks, filename = 'blockchain_audit_trail.csv') {
  const HDR = ['Block#', 'Action', 'Severity', 'User', 'Timestamp', 'Details', 'Block Hash', 'Prev Hash', 'Nonce'];
  const rows = blocks.map(b => [
    b.blockIndex,
    b.action,
    (b.severity || 'low').toUpperCase(),
    b.user || '',
    b.timestamp || '',
    (b.details || '').replace(/"/g, '""'),
    b.blockHash  || '',
    b.previousHash || '',
    b.nonce || 0,
  ]);
  const csv = [HDR.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
}

async function exportPDF(blocks, stats) {
  const { jsPDF } = await import('jspdf');
  const autoTable  = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'A4' });

  // ── Title block ──────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.text('MSN-CLIQ vEritEqcLiQ — Blockchain Audit Trail', 40, 40);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(`SHA-256 · Proof-of-Work · Immutable Ledger`, 40, 54);
  doc.text(`Generated: ${new Date().toLocaleString()}  |  Total Blocks: ${stats.total ?? blocks.length}  |  Chain: ${stats.chainValid !== false ? 'VALID' : 'COMPROMISED'}`, 40, 64);
  doc.text(`Exported Rows: ${blocks.length}`, 40, 74);

  // ── Horizontal rule ───────────────────────────────────────────────────────
  doc.setDrawColor(226, 232, 240);
  doc.line(40, 80, 801, 80);

  // ── Data table ────────────────────────────────────────────────────────────
  const SEV_COLORS = {
    CRITICAL: [239, 68,  68],
    HIGH:     [249, 115, 22],
    MEDIUM:   [245, 158, 11],
    LOW:      [148, 163, 184],
  };

  autoTable(doc, {
    startY: 88,
    margin: { left: 40, right: 40 },
    styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
    headStyles: {
      fillColor: [15, 23, 42], textColor: 255,
      fontStyle: 'bold', fontSize: 7.5
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 34,  halign: 'center' },
      1: { cellWidth: 90 },
      2: { cellWidth: 50,  halign: 'center' },
      3: { cellWidth: 70 },
      4: { cellWidth: 100 },
      5: { cellWidth: 'auto' },
      6: { cellWidth: 95,  font: 'courier', fontSize: 6 },
    },
    head: [['#', 'Action', 'Severity', 'User', 'Timestamp', 'Details', 'Block Hash']],
    body: blocks.map(b => [
      b.blockIndex,
      b.action,
      (b.severity || 'low').toUpperCase(),
      b.user || 'System',
      b.timestamp ? new Date(b.timestamp).toLocaleString() : '—',
      b.details || '',
      b.blockHash ? `${b.blockHash.slice(0, 16)}…` : '',
    ]),
    didParseCell(data) {
      // Colour severity column
      if (data.column.index === 2 && data.section === 'body') {
        const sev = String(data.cell.raw);
        const rgb = SEV_COLORS[sev] || SEV_COLORS.LOW;
        data.cell.styles.textColor = rgb;
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // ── Footer on every page ──────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `MSN-CLIQ vEritEqcLiQ · Confidential · Page ${i} of ${totalPages}`,
      doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 20,
      { align: 'center' }
    );
  }

  doc.save(`blockchain_audit_trail_${Date.now()}.pdf`);
}

/* ── Action type meta ──────────────────────────────────────────────────── */
const ACTION_META = {
  AUDIT_LOG:                      { label: 'Audit Log',      color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  icon: <Activity size={10} /> },
  LICENSE_ISSUANCE:               { label: 'License',        color: '#10b981', bg: 'rgba(16,185,129,0.1)',  icon: <FileKey size={10} /> },
  TIER_INTEGRITY_TAMPER_DETECTED: { label: 'Tamper!',        color: '#ef4444', bg: 'rgba(239,68,68,0.12)', icon: <AlertTriangle size={10} /> },
  MODULE_REGISTRATION:            { label: 'Module Reg.',    color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)',  icon: <Cpu size={10} /> },
  GENESIS:                        { label: 'Genesis',        color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: <GitCommit size={10} /> },
};
const DEFAULT_META = { label: 'Block', color: '#6b7280', bg: 'rgba(107,114,128,0.1)', icon: <Hash size={10} /> };
const meta = (a) => ACTION_META[a] || DEFAULT_META;

/* ── Severity meta ─────────────────────────────────────────────────────── */
const SEV_META = {
  critical: { label: 'CRITICAL', color: '#ef4444', dot: '#ef4444' },
  high:     { label: 'HIGH',     color: '#f97316', dot: '#f97316' },
  medium:   { label: 'MED',      color: '#f59e0b', dot: '#f59e0b' },
  low:      { label: 'LOW',      color: '#6b7280', dot: '#94a3b8' },
};
const sevMeta = (s) => SEV_META[(s || 'low').toLowerCase()] || SEV_META.low;

/* ── Copy helper ────────────────────────────────────────────────────────── */
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });
  };
  return (
    <button onClick={copy} title="Copy" style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px',
      color: copied ? '#10b981' : 'var(--text-secondary)', transition: 'color 150ms'
    }}>
      {copied ? <CheckCheck size={11} /> : <Copy size={11} />}
    </button>
  );
}

/* ── Hash display ───────────────────────────────────────────────────────── */
function HashDisplay({ hash, label, color }) {
  if (!hash) return null;
  const short = hash.length > 20 ? `${hash.slice(0, 10)}…${hash.slice(-10)}` : hash;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
      <span style={{ fontSize: 9, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', minWidth: 70 }}>{label}</span>
      <code style={{
        fontFamily: 'var(--font-mono, monospace)', fontSize: 10, padding: '1px 6px',
        background: 'var(--surface-3)', borderRadius: 3, color: color || 'var(--text-primary)',
        border: '1px solid var(--border)', letterSpacing: '0.04em'
      }}>{hash}</code>
      <CopyBtn text={hash} />
    </div>
  );
}

/* ── Individual block ───────────────────────────────────────────────────── */
function BlockEntry({ block, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const m = meta(block.action);
  const sm = sevMeta(block.severity);
  const isGenesis = block.blockIndex === 0;
  const isTamper  = block.action === 'TIER_INTEGRITY_TAMPER_DETECTED';
  const ts = block.timestamp ? new Date(block.timestamp) : null;
  const dateStr = ts ? ts.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const timeStr = ts ? ts.toLocaleTimeString([], { hour12: false }) : '—';

  return (
    <div style={{ display: 'flex', gap: 0 }}>
      {/* Chain link column */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0, paddingTop: 12 }}>
        <div style={{
          width: 22, height: 22, borderRadius: '50%', border: `2px solid ${m.color}`,
          background: isGenesis ? m.color : `${m.color}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1
        }}>
          {isGenesis
            ? <GitCommit size={10} color="#fff" />
            : <span style={{ fontSize: 8, fontWeight: 800, color: m.color }}>#{block.blockIndex}</span>}
        </div>
        {!isLast && (
          <div style={{ flex: 1, width: 2, background: `linear-gradient(to bottom, ${m.color}40, var(--border))`, minHeight: 20, marginTop: 2 }} />
        )}
      </div>

      {/* Block card */}
      <div style={{
        flex: 1, marginLeft: 8, marginBottom: 10,
        background: isTamper ? 'rgba(239,68,68,0.06)' : isGenesis ? 'rgba(245,158,11,0.04)' : 'var(--surface)',
        border: `1px solid ${isTamper ? 'rgba(239,68,68,0.3)' : isGenesis ? 'rgba(245,158,11,0.25)' : 'var(--border)'}`,
        borderRadius: 8, overflow: 'hidden',
        boxShadow: isTamper ? '0 0 0 1px rgba(239,68,68,0.15)' : 'none'
      }}>
        {/* Compact header (always visible) */}
        <div
          onClick={() => setExpanded(e => !e)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', userSelect: 'none' }}
        >
          {/* Block # */}
          <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--text-secondary)', minWidth: 36, flexShrink: 0 }}>
            #{block.blockIndex}
          </span>

          {/* Action badge */}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '1px 7px', borderRadius: 12, fontSize: 9, fontWeight: 700,
            background: m.bg, color: m.color, flexShrink: 0
          }}>
            {m.icon}{m.label}
          </span>

          {/* Severity dot */}
          <span title={sm.label} style={{
            width: 7, height: 7, borderRadius: '50%', background: sm.dot, flexShrink: 0
          }} />

          {/* Details preview */}
          <span style={{ flex: 1, fontSize: 11, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {block.details}
          </span>

          {/* User */}
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text-secondary)', flexShrink: 0 }}>
            <User size={9} /> {block.user}
          </span>

          {/* Timestamp */}
          <span style={{ fontSize: 10, color: 'var(--text-secondary)', flexShrink: 0, fontFamily: 'var(--font-mono, monospace)' }}>
            {dateStr} {timeStr}
          </span>

          {/* Hash preview */}
          <code style={{ fontSize: 9, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono, monospace)', flexShrink: 0 }}>
            {block.blockHash ? block.blockHash.slice(0, 8) + '…' : ''}
          </code>

          {/* Expand toggle */}
          <div style={{ color: 'var(--text-secondary)', flexShrink: 0, transition: 'transform 150ms', transform: expanded ? 'rotate(90deg)' : 'none' }}>
            <ChevronRight size={12} />
          </div>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div style={{ padding: '8px 14px 12px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)' }}>
            {/* Full details text */}
            <div style={{ fontSize: 12, color: 'var(--text-primary)', marginBottom: 10, lineHeight: 1.6, padding: '6px 10px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
              {block.details}
            </div>

            {/* Hash fields */}
            <HashDisplay hash={block.blockHash}    label="Block Hash"  color={m.color} />
            <HashDisplay hash={block.previousHash} label="Prev Hash"   color="#64748b" />

            {/* Nonce + Severity row */}
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 10, flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 700, marginRight: 4 }}>Nonce</span>
                <code style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>{block.nonce?.toLocaleString() || '0'}</code>
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 700, marginRight: 4 }}>Severity</span>
                <span style={{ color: sm.color, fontWeight: 700 }}>{(block.severity || 'low').toUpperCase()}</span>
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 700, marginRight: 4 }}>Timestamp</span>
                <code style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>{block.timestamp}</code>
              </span>
            </div>

            {/* Raw data (collapsible) */}
            {block.rawData && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ fontSize: 10, cursor: 'pointer', color: 'var(--text-secondary)', userSelect: 'none' }}>Raw block data</summary>
                <pre style={{
                  marginTop: 6, padding: '8px 10px', background: 'var(--surface-3)',
                  borderRadius: 6, fontSize: 10, fontFamily: 'var(--font-mono, monospace)',
                  color: 'var(--text-primary)', overflowX: 'auto', border: '1px solid var(--border)',
                  maxHeight: 160, overflowY: 'auto'
                }}>{JSON.stringify(block.rawData, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────────── */
export default function AuditVault() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [sevFilter, setSevFilter]       = useState('all');
  const [exporting, setExporting]       = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiFetch(`${API}/audit/trail`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      // Handle both old (array) and new ({chain, stats}) response shapes
      if (Array.isArray(d)) { setData({ chain: d, stats: { total: d.length, chainValid: true } }); }
      else { setData(d); }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data?.chain) return [];
    const q = search.trim().toLowerCase();
    return data.chain.filter(b => {
      if (actionFilter !== 'all' && b.action !== actionFilter) return false;
      if (sevFilter !== 'all' && (b.severity || 'low').toLowerCase() !== sevFilter) return false;
      if (!q) return true;
      return (
        String(b.blockIndex).includes(q) ||
        (b.action || '').toLowerCase().includes(q) ||
        (b.user  || '').toLowerCase().includes(q) ||
        (b.details || '').toLowerCase().includes(q) ||
        (b.blockHash || '').toLowerCase().includes(q)
      );
    });
  }, [data, search, actionFilter, sevFilter]);

  const stats = data?.stats || {};
  const chainValid = stats.chainValid !== false;
  const actionTypes = useMemo(() => ['all', ...Object.keys(stats.actionCounts || {})], [stats]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', overflowY: 'auto' }}>

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div style={{
        padding: '16px 20px 12px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface-2)',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {chainValid
              ? <ShieldCheck size={22} color="#10b981" />
              : <ShieldAlert size={22} color="#ef4444" />}
            <div>
              <h1 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '0.02em' }}>
                MSN-CLIQ Blockchain Audit Trail
              </h1>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 1 }}>
                SHA-256 · Proof-of-Work · Difficulty 2 · Immutable Ledger
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              data-testid="audit-export-csv"
              className="erp-btn"
              onClick={() => { exportCSV(filtered); }}
              disabled={loading || filtered.length === 0}
              title="Export visible blocks to CSV"
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}
            >
              <FileSpreadsheet size={12} /> CSV
            </button>
            <button
              data-testid="audit-export-pdf"
              className="erp-btn"
              onClick={async () => {
                setExporting('pdf');
                try { await exportPDF(filtered, stats); }
                catch (e) { console.error('PDF export failed:', e); }
                finally { setExporting(''); }
              }}
              disabled={loading || filtered.length === 0 || exporting === 'pdf'}
              title="Export visible blocks to PDF"
              style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}
            >
              <FileText size={12} /> {exporting === 'pdf' ? 'Building…' : 'PDF'}
            </button>
            <button className="erp-btn" onClick={load} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <StatChip icon={<Hash size={12} />} label="Total Blocks" value={stats.total ?? '—'} />
          <StatChip
            icon={chainValid ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
            label="Chain Integrity"
            value={chainValid ? 'VALID' : 'COMPROMISED'}
            color={chainValid ? '#10b981' : '#ef4444'}
          />
          <StatChip icon={<GitCommit size={12} />} label="Genesis" value={stats.genesisDate ? new Date(stats.genesisDate).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : '—'} />
          <StatChip icon={<Clock size={12} />} label="Latest Block" value={stats.latestDate ? new Date(stats.latestDate).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : '—'} />
          {Object.entries(stats.actionCounts || {}).map(([k, v]) => {
            const m = meta(k);
            return <StatChip key={k} icon={m.icon} label={m.label} value={v} color={m.color} />;
          })}
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 150 }}>
            <Search size={11} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
            <input
              className="form-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search blocks, hashes, users…"
              style={{ paddingLeft: 26, fontSize: 11, height: 28, width: '100%' }}
            />
          </div>
          <select className="form-input" value={actionFilter} onChange={e => setActionFilter(e.target.value)} style={{ fontSize: 11, height: 28, minWidth: 130 }}>
            <option value="all">All Actions</option>
            {actionTypes.filter(t => t !== 'all').map(t => (
              <option key={t} value={t}>{meta(t).label}</option>
            ))}
          </select>
          <select className="form-input" value={sevFilter} onChange={e => setSevFilter(e.target.value)} style={{ fontSize: 11, height: 28, minWidth: 110 }}>
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
            {filtered.length} of {stats.total ?? 0} blocks
          </span>
        </div>
      </div>

      {/* ── Chain list ──────────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: '16px 20px', overflowY: 'auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', paddingTop: 40, color: 'var(--text-secondary)', fontSize: 13 }}>
            <Loader size={18} className="spin" style={{ marginBottom: 8 }} /><br />Loading blockchain…
          </div>
        )}
        {error && (
          <div style={{ padding: 16, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#ef4444', fontSize: 12 }}>
            ⚠ {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign: 'center', paddingTop: 40, color: 'var(--text-secondary)', fontSize: 13 }}>
            No blocks match your filters.
          </div>
        )}
        {!loading && !error && filtered.map((block, i) => (
          <BlockEntry key={block.blockHash || i} block={block} isLast={i === filtered.length - 1} />
        ))}
      </div>
    </div>
  );
}

function StatChip({ icon, label, value, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
      fontSize: 11
    }}>
      <span style={{ color: color || 'var(--text-secondary)' }}>{icon}</span>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontWeight: 700, color: color || 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}
